import {
  Building2,
  Landmark,
  Users,
  UserCheck,
  AlertCircle,
  Check,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import type { DiscoveryImportPreview } from "@shared/discovery-types";

interface ImportPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: DiscoveryImportPreview | null;
  isLoading?: boolean;
  onConfirm: () => void;
  isCommitting?: boolean;
}

/**
 * Dialog showing a summary of what will be created when committing
 *
 * Shows counts for each section, lists any excluded items,
 * and shows assumptions with user overrides
 */
export function ImportPreviewDialog({
  open,
  onOpenChange,
  preview,
  isLoading = false,
  onConfirm,
  isCommitting = false,
}: ImportPreviewDialogProps) {
  if (!preview && !isLoading) return null;

  const hasOverrides = preview?.summary?.overriddenAssumptions
    ? preview.summary.overriddenAssumptions > 0
    : false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Confirm Import</DialogTitle>
          <DialogDescription>
            Review what will be created when you confirm
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center text-muted-foreground">
            Loading preview...
          </div>
        ) : preview ? (
          <div className="space-y-4">
            {/* Company Profile */}
            <SectionPreview
              icon={<Building2 className="h-4 w-4" />}
              label="Company Profile"
              enabled={preview.companyProfile.enabled}
              willCreate={preview.companyProfile.willCreate}
              detail={
                preview.companyProfile.data?.name
                  ? `${preview.companyProfile.data.name}${
                      preview.companyProfile.data.uid
                        ? ` (${preview.companyProfile.data.uid})`
                        : ""
                    }`
                  : undefined
              }
            />

            <Separator />

            {/* Bank Accounts */}
            <SectionPreview
              icon={<Landmark className="h-4 w-4" />}
              label="Bank Accounts"
              enabled={preview.bankAccounts.enabled}
              willCreateCount={preview.bankAccounts.willCreateCount}
              totalCount={preview.bankAccounts.totalCount}
              excludedCount={preview.bankAccounts.excludedCount}
            />

            {/* Vendors */}
            <SectionPreview
              icon={<Users className="h-4 w-4" />}
              label="Vendors"
              enabled={preview.vendors.enabled}
              willCreateCount={preview.vendors.willCreateCount}
              totalCount={preview.vendors.totalCount}
              excludedCount={preview.vendors.excludedCount}
            />

            {/* Customers */}
            <SectionPreview
              icon={<UserCheck className="h-4 w-4" />}
              label="Customers"
              enabled={preview.customers.enabled}
              willCreateCount={preview.customers.willCreateCount}
              totalCount={preview.customers.totalCount}
              excludedCount={preview.customers.excludedCount}
            />

            {/* Assumptions with Overrides */}
            {hasOverrides && (
              <>
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <AlertCircle className="h-4 w-4 text-warning" />
                    <span className="font-medium">
                      {preview.summary.overriddenAssumptions} assumption
                      {preview.summary.overriddenAssumptions !== 1
                        ? "s"
                        : ""}{" "}
                      overridden
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Your custom values will be used instead of the extracted
                    values
                  </div>
                </div>
              </>
            )}

            {/* Summary */}
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="text-sm font-medium mb-2">Summary</div>
              <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                <div>
                  Company:{" "}
                  <span className="font-medium text-foreground">
                    {preview.summary.willCreateCompany ? "Yes" : "No"}
                  </span>
                </div>
                <div>
                  Bank Accounts:{" "}
                  <span className="font-medium text-foreground">
                    {preview.summary.totalBankAccounts}
                  </span>
                </div>
                <div>
                  Vendors:{" "}
                  <span className="font-medium text-foreground">
                    {preview.summary.totalVendors}
                  </span>
                </div>
                <div>
                  Customers:{" "}
                  <span className="font-medium text-foreground">
                    {preview.summary.totalCustomers}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isCommitting || isLoading}>
            {isCommitting ? (
              "Creating..."
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Confirm & Create
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface SectionPreviewProps {
  icon: React.ReactNode;
  label: string;
  enabled: boolean;
  willCreate?: boolean;
  willCreateCount?: number;
  totalCount?: number;
  excludedCount?: number;
  detail?: string;
}

function SectionPreview({
  icon,
  label,
  enabled,
  willCreate,
  willCreateCount,
  totalCount,
  excludedCount = 0,
  detail,
}: SectionPreviewProps) {
  const isCountMode = willCreateCount !== undefined;
  const hasExclusions = excludedCount > 0;

  return (
    <div className={cn("flex items-center gap-3", !enabled && "opacity-50")}>
      <div
        className={cn(
          "flex h-8 w-8 items-center justify-center rounded-full",
          enabled
            ? "bg-primary/10 text-primary"
            : "bg-muted text-muted-foreground"
        )}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium">{label}</span>
          {!enabled && (
            <Badge variant="secondary" className="text-xs">
              Disabled
            </Badge>
          )}
        </div>
        {enabled && (
          <div className="text-sm text-muted-foreground">
            {isCountMode ? (
              <>
                {willCreateCount} of {totalCount} will be created
                {hasExclusions && (
                  <span className="text-warning">
                    {" "}
                    ({excludedCount} excluded)
                  </span>
                )}
              </>
            ) : willCreate ? (
              detail || "Will be created"
            ) : (
              <span className="italic">No data to create</span>
            )}
          </div>
        )}
      </div>
      <div>
        {enabled ? (
          <Check className="h-4 w-4 text-success" />
        ) : (
          <X className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
    </div>
  );
}
